import { AdminSuppression } from 'libs/end-to-end/src/admin';
import { AdminSuppressions } from 'libs/end-to-end/src/admin';
import { DeleteAdminSuppression } from 'libs/end-to-end/src/admin';
import { GenericSuccess } from 'libs/end-to-end/src/generic';
import React, { useEffect, useMemo, useState } from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTable } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTableRow } from 'apps/sigma2-ui/src/components/AdminLayout';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

export const EmailsPage = () => {
  const { myUser, userAuthenticatedApiCall } = useUser();

  const [suppressions, setSuppressions] = useState<AdminSuppression[] | null>(
    null,
  );
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingSuppression, setIsDeletingSuppression] = useState(false);

  const loadPageOfSuppressions = useAsyncCallback(
    async (isAlive: IsAliveFn) => {
      if (!isAlive()) {
        return;
      }

      setIsLoading(true);

      try {
        let url = `${getApiBaseUrl()}/admin/suppressions`;

        if (nextToken) {
          url += `?nextToken=${encodeURIComponent(nextToken)}`;
        }

        const getSuppressionsResult = await userAuthenticatedApiCall({
          url,
          requestOptions: {
            method: 'GET',
          },
          responseSchema: AdminSuppressions,
        });

        if (!isAlive()) {
          return;
        }

        if (
          !getSuppressionsResult ||
          getSuppressionsResult.resultType === 'err'
        ) {
          return;
        }

        const { suppressions: newSuppressions, nextToken: newNextToken } =
          getSuppressionsResult.value;

        setSuppressions((prevSuppressions) => {
          if (!prevSuppressions) {
            return [...newSuppressions];
          }

          return [...prevSuppressions, ...newSuppressions];
        });

        setNextToken(newNextToken);
      } finally {
        setIsLoading(false);
      }
    },
    [nextToken, userAuthenticatedApiCall],
  );

  useEffect(() => {
    if (!myUser || myUser.roleAssignments !== 'superAdmin') {
      return;
    }

    loadPageOfSuppressions();
  }, [myUser, loadPageOfSuppressions]);

  const deleteSuppression = useAsyncCallback(
    async (isAlive: IsAliveFn, emailAddress: string) => {
      if (!isAlive() || isDeletingSuppression) {
        return;
      }

      try {
        setIsDeletingSuppression(true);

        const deleteRequest: DeleteAdminSuppression = {
          emailAddress,
        };

        const deleteUserResult = await userAuthenticatedApiCall({
          url: `${getApiBaseUrl()}/admin/suppressions`,
          requestOptions: {
            method: 'DELETE',
            body: JSON.stringify(deleteRequest),
          },
          responseSchema: GenericSuccess,
        });

        if (!isAlive()) {
          return;
        }

        if (!deleteUserResult || deleteUserResult.resultType === 'err') {
          return;
        }

        setSuppressions((prevSuppressions) => {
          if (!prevSuppressions) {
            return [];
          }

          return prevSuppressions.filter(
            (suppression) => suppression.emailAddress !== emailAddress,
          );
        });
      } finally {
        setIsDeletingSuppression(false);
      }
    },
    [userAuthenticatedApiCall, isDeletingSuppression],
  );

  const rows = useMemo(() => {
    if (!suppressions) {
      return [];
    }

    const actualRows: AdminTableRow[] = [];

    for (const suppression of suppressions) {
      actualRows.push({
        key: suppression.emailAddress,
        columns: [
          {
            columnType: 'text',
            key: 'email',
            text: suppression.emailAddress,
          },
          {
            columnType: 'text',
            key: 'reason',
            text: suppression.reason ?? 'None',
          },
          {
            columnType: 'text',
            key: 'timestamp',
            text: new Date(suppression.lastUpdateTime).toLocaleString(),
          },
          {
            columnType: 'button',
            key: 'delete',
            buttonText: 'Delete',
            onClick: () => deleteSuppression(suppression.emailAddress),
          },
        ],
      });
    }
    return actualRows;
  }, [suppressions, deleteSuppression]);

  const ui = useMemo(() => {
    if (isDeletingSuppression) {
      return <p>Deleting user...</p>;
    }

    let nextButton: React.ReactNode = <></>;

    if (nextToken) {
      nextButton = (
        <LegacyButton
          variant={LegacyButtonVariant.PRIMARY_LIGHT}
          disabled={isDeletingSuppression || isLoading}
          className="mt-4"
          onClick={() => {
            loadPageOfSuppressions();
          }}
        >
          Load More
        </LegacyButton>
      );
    }

    return (
      <>
        <AdminBreadcrumbs
          items={[
            { label: 'Home', to: '/admin' },
            { label: 'Emails', to: '/emails' },
          ]}
        />
        <p>
          {' '}
          You can manage emails (bounced emails lead to temporarily muted email
          addresses) from this screen.
        </p>

        <AdminTable
          headings={['Email', 'Reason', 'Timestamp', 'Delete']}
          rows={rows}
        />

        {nextButton}
      </>
    );
  }, [
    rows,
    isDeletingSuppression,
    isLoading,
    loadPageOfSuppressions,
    nextToken,
  ]);

  return <AdminLayout currentPage={'Emails'}>{ui}</AdminLayout>;
};
